import { ApiError } from '../../lib/api';
import { declaredAssetType } from '../../lib/portalAssets/allowlist';
import { DETECT_HEAD_BYTES, detectAssetType } from '../../lib/portalAssets/detect';
import { duplicateCheckNotice, planDuplicateSkips } from '../../lib/portalAssets/duplicates';
import { byteContradictionRefusal, planUploadFiles, uploadBatches, UPLOAD_CONCURRENCY } from '../../lib/portalAssets/uploadPlan';
import type { PortalAssetsApi } from './assetsApi';
import { uploadResumable, type FetchLike } from './resumableUpload';

// THE UPLOAD RUN for one batch of chosen files (spec §4.2-§4.5): plan → ask the
// server for signed sessions (≤ 50 files per request) → upload ≤ 4 at once →
// tell the server each one is complete. Independent of React, so it is tested
// in node against a fake API and a fake GCS.
//
// ⚠️ ONE FILE NEVER FAILS ANOTHER. A refused type, a dropped session or a 409 on
// one file is recorded on that file's row; the rest carry on.
//
// ⚠️ `done` means UPLOADED, not READY. Processing happens on the server after
// the bucket notification fires, and its outcome (including a failure the bytes
// decide, e.g. a .jpg that is really HTML) arrives later, in the grid.

export type UploadRowStatus = 'refused' | 'waiting' | 'uploading' | 'done' | 'failed';

export type UploadRow = {
  key: number;
  filename: string;
  sizeBytes: number;
  status: UploadRowStatus;
  /** 0..1 while uploading. */
  progress: number;
  reason: string | null;
};

function messageOf(err: unknown): string {
  if (err instanceof ApiError || err instanceof Error) return err.message;
  return 'Something went wrong.';
}

async function runPool<T>(items: readonly T[], width: number, fn: (item: T) => Promise<void>): Promise<void> {
  let next = 0;
  const lanes = Array.from({ length: Math.min(width, items.length) }, async () => {
    while (next < items.length) {
      const item = items[next++]!;
      await fn(item);
    }
  });
  await Promise.all(lanes);
}

export async function uploadAssetFiles(input: {
  api: Pick<PortalAssetsApi, 'createUploads' | 'complete'>;
  accountId: string;
  consentWordingVersion: string;
  /** Staff uploader only. Sent ONLY when true, so an ordinary request is byte-identical to before. */
  approveOnUpload?: boolean;
  files: readonly File[];
  onChange: (rows: UploadRow[]) => void;
  /** Called with ONE sentence when the server's duplicate check did not run (see PortalAssetUploadResponse.duplicateCheckRan). */
  onNotice?: (sentence: string) => void;
  fetchImpl?: FetchLike;
  sleep?: (ms: number) => Promise<void>;
  chunkBytes?: number;
}): Promise<UploadRow[]> {
  const plan = planUploadFiles(input.files.map((f) => ({ name: f.name, type: f.type, size: f.size })));
  // ⚠️ Byte check BEFORE anything is sent (macOS/iOS sidecars like thumb_<uuid>.mov are photos
  // wearing a video extension). A contradiction becomes a NAMED refused row with its reason — never
  // a silent drop. A file we cannot read, or cannot recognise, is kept and uploads as before.
  await runPool(plan, 8, async (p) => {
    if (!p.accepted) return;
    const file = input.files[p.index]!;
    const declared = declaredAssetType(file.name, file.type);
    if (!declared) return;
    try {
      const head = new Uint8Array(await file.slice(0, DETECT_HEAD_BYTES).arrayBuffer());
      const refusal = byteContradictionRefusal(file.name, declared.mediaType, detectAssetType(head));
      if (refusal) plan[p.index] = { index: p.index, filename: p.filename, accepted: false, reason: refusal };
    } catch {
      /* unreadable head: keep it; the worker decides from the bytes later */
    }
  });
  // ⚠️ COURTESY duplicate check — within this selection only (the same file chosen twice). The authoritative
  // check against the club's existing assets is the server's; its skips come back below and are shown the same way.
  const chosen = plan.filter((p): p is Extract<typeof p, { accepted: true }> => p.accepted);
  for (const d of planDuplicateSkips(chosen.map((p) => ({ filename: p.filename, sizeBytes: p.sizeBytes })), [])) {
    const p = chosen[d.index]!;
    plan[p.index] = { index: p.index, filename: p.filename, accepted: false, reason: d.reason };
  }
  const rows: UploadRow[] = plan.map((p) => ({
    key: p.index,
    filename: p.filename,
    sizeBytes: input.files[p.index]!.size,
    status: p.accepted ? 'waiting' : 'refused',
    progress: 0,
    reason: p.accepted ? null : p.reason,
  }));
  const emit = () => input.onChange(rows.map((r) => ({ ...r })));
  const set = (key: number, patch: Partial<UploadRow>) => {
    Object.assign(rows[key]!, patch);
    emit();
  };
  emit();

  const accepted = plan.filter((p): p is Extract<typeof p, { accepted: true }> => p.accepted);
  const work: Array<{ key: number; assetId: string; uploadUrl: string; requiredHeaders: Record<string, string> }> = [];

  for (const batch of uploadBatches(accepted)) {
    try {
      const response = await input.api.createUploads({
        accountId: input.accountId,
        consentWordingVersion: input.consentWordingVersion,
        ...(input.approveOnUpload ? { approveOnUpload: true } : {}),
        files: batch.map((p) => ({ filename: p.filename, contentType: p.contentType, sizeBytes: p.sizeBytes })),
      });
      // ⚠️ The tickets come back in REQUEST order. A count mismatch means the
      // pairing cannot be trusted, and uploading file A into file B's session
      // would store one club's photo under another file's name.
      // ⚠️ The server DROPS duplicates (naming each, with its request index) and returns tickets for the rest, so
      // the pairing is "the batch minus the skipped indexes", and every skipped file is SHOWN with its sentence.
      const { uploads, skipped } = response;
      const notice = duplicateCheckNotice(response);
      if (notice !== null) input.onNotice?.(notice);
      const skippedAt = new Map((skipped ?? []).map((s) => [s.index, s.reason]));
      const kept = batch.filter((_, i) => !skippedAt.has(i));
      if (uploads.length !== kept.length) throw new Error('The server returned an unexpected number of uploads.');
      batch.forEach((p, i) => {
        const reason = skippedAt.get(i);
        if (reason !== undefined) set(p.index, { status: 'refused', reason });
      });
      kept.forEach((p, i) => work.push({ key: p.index, ...uploads[i]! }));
    } catch (err) {
      for (const p of batch) set(p.index, { status: 'failed', reason: messageOf(err) });
    }
  }

  await runPool(work, UPLOAD_CONCURRENCY, async (w) => {
    set(w.key, { status: 'uploading', progress: 0 });
    try {
      await uploadResumable({
        file: input.files[w.key]!,
        uploadUrl: w.uploadUrl,
        requiredHeaders: w.requiredHeaders,
        fetchImpl: input.fetchImpl,
        sleep: input.sleep,
        chunkBytes: input.chunkBytes,
        onProgress: (sent, total) => set(w.key, { progress: total > 0 ? sent / total : 1 }),
      });
    } catch (err) {
      set(w.key, { status: 'failed', reason: messageOf(err) });
      return;
    }
    try {
      await input.api.complete(w.assetId);
      set(w.key, { status: 'done', progress: 1 });
    } catch (err) {
      // ⚠️ A 409 is the server saying the stored object does not match what was
      // declared — a real failure. Anything else (a dropped connection on the
      // complete call) leaves an uploaded file the bucket notification will
      // still process, so it is reported as uploaded, not as failed.
      if (err instanceof ApiError && err.status === 409) set(w.key, { status: 'failed', reason: err.message });
      else set(w.key, { status: 'done', progress: 1 });
    }
  });

  return rows.map((r) => ({ ...r }));
}

/** The one-line summary under the file list. */
export function uploadSummary(rows: readonly UploadRow[]): string | null {
  if (rows.length === 0) return null;
  const n = (s: UploadRowStatus) => rows.filter((r) => r.status === s).length;
  const active = n('waiting') + n('uploading');
  if (active > 0) return `Uploading ${n('done') + n('uploading')} of ${rows.length - n('refused')}… keep this page open until uploads finish.`;
  const parts = [`${n('done')} uploaded`];
  if (n('failed')) parts.push(`${n('failed')} failed`);
  if (n('refused')) parts.push(`${n('refused')} not accepted`);
  return `${parts.join(' · ')}. Uploaded files appear in your library once processed.`;
}
